import { Link } from "react-router-dom"
import { API_URL, URLS } from "../_config"
import Footer from "../components/layouts/footer"
import Nav from "../components/layouts/nav"
import MetaSection from "../components/MetaSection"
import { IMAGE_FETCH_BACKEND_URL } from "../_config/site_urls"
import { buildHomeStructuredData } from "../utils/structuredData"
import { useEffect, useState, useRef } from "react"
import { applyButtonGroupStyles } from "../utils/applyButtonGroupStyles"
import { scopeCustomCss } from "../utils/scopeCustomCss"

// Fixed id, since there's only ever one homepage — used both as the DOM
// wrapper id and as the CSS scope prefix for custom_css.
const HOME_WRAPPER_ID = "home-page-content"

const Home = () => {
  const [subscriptionPlanType, setSubscriptionPlanType] = useState<any>(null);
  const [homeContantData, setHomeContantData] = useState<any>(null);
  const [homeContent, setHomeContent] = useState<any>(null)
  const [processedHtml, setProcessedHtml] = useState<string>("")
  const [hasFaqAccordion, setHasFaqAccordion] = useState<boolean>(false)
  const contentRef = useRef<HTMLDivElement>(null)

  const getHomeContentData = async () => {
    try {
      const response = await fetch((API_URL.HOME_CONTENT_DATA));
      const resp = await response.json();
      // console.log(resp.data);
      setHomeContantData(resp.data)
    } catch (error) {
      console.log('Error occurred while making the API request.');
      // Handle error
      setHomeContantData([])
    }
  }

  const getSubscriptionPlanType = async () => {
    try {
      const response = await fetch(API_URL.SUBSCRIPTION.PLAN_TYPE_LIST);
      const resp = await response.json();
      setSubscriptionPlanType(resp.data)
    } catch (error) {
      setSubscriptionPlanType([])
    }
  };

  const getHomeContent = async () => {
    try {
      const response = await fetch(API_URL.HOME_PAGE_CONTENT)
      const resp = await response.json()
      setHomeContent(resp.data)
    } catch (error) {
      console.log("Error occurred while fetching homepage content.")
      setHomeContent(null)
    }
  }

  useEffect(() => {
    // getHomeContentData();
    // getSubscriptionPlanType();
    getHomeContent();
  }, [])

  // Same FAQ/accordion HTML transform used on Menu Pages: if the admin
  // drops an Accordion block styled as an FAQ into the content, it still
  // renders as a working Bootstrap accordion here too.
  const processHtmlForAccordion = (htmlString: string) => {
    if (!htmlString) return { html: "", hasFaq: false }

    const parser = new DOMParser()
    const doc = parser.parseFromString(htmlString, "text/html")

    const faqContainers = doc.querySelectorAll("div.faq-accordion, div[class*='faq-accordion']")
    const hasFaq = faqContainers.length > 0

    if (hasFaq) {
      const style = doc.createElement("style")
      style.textContent = `
                .faq-accordian .accordion-button::after {
                    display: none !important;
                }
                .faq-accordian .accordion-button {
                    display: flex !important;
                    justify-content: space-between !important;
                    align-items: center !important;
                }
            `
      doc.head.appendChild(style)

      faqContainers.forEach((container, containerIndex) => {
        container.classList.add("accordion", "faq-accordian")
        container.setAttribute("id", `homeAccordion${containerIndex}`)

        const faqItems = container.querySelectorAll(".faq-item")
        let itemIndex = 0

        faqItems.forEach((item: Element) => {
          const questionContainer = item.querySelector(".faq-question")
          const answerContainer = item.querySelector(".faq-answer")

          if (questionContainer && answerContainer) {
            item.classList.add("accordion-item")

            const questionHeading = questionContainer.querySelector("h2,h4, h3, h6, h5")
            let questionText = ""

            if (questionHeading) {
              questionText = questionHeading.textContent || questionHeading.innerHTML || ""
            } else {
              const questionClone = questionContainer.cloneNode(true) as Element
              const toggle = questionClone.querySelector(".faq-toggle")
              if (toggle) toggle.remove()
              questionText = questionClone.textContent || questionClone.innerHTML || ""
            }

            const toggle = questionContainer.querySelector(".faq-toggle")
            if (toggle) toggle.remove()

            const headerId = `heading${containerIndex}_${itemIndex}`
            const collapseId = `collapse${containerIndex}_${itemIndex}`

            const accordionHeader = doc.createElement("h2")
            accordionHeader.className = "accordion-header"
            accordionHeader.id = headerId

            const button = doc.createElement("button")
            button.className = "accordion-button collapsed"
            button.type = "button"
            button.setAttribute("data-bs-toggle", "collapse")
            button.setAttribute("data-bs-target", `#${collapseId}`)
            button.setAttribute("aria-expanded", "false")
            button.setAttribute("aria-controls", collapseId)

            const questionSpan = doc.createElement("span")
            questionSpan.textContent = questionText.trim()
            questionSpan.style.flex = "1"
            questionSpan.style.textAlign = "left"
            questionSpan.style.paddingRight = "10px"
            button.appendChild(questionSpan)

            const iconSpan = doc.createElement("span")
            iconSpan.className = "faq-accordion-icon"
            iconSpan.style.marginLeft = "auto"
            iconSpan.style.fontSize = "1.5em"
            iconSpan.style.fontWeight = "bold"
            iconSpan.style.flexShrink = "0"
            iconSpan.style.lineHeight = "1"
            iconSpan.style.minWidth = "24px"
            iconSpan.style.textAlign = "center"
            iconSpan.style.display = "inline-flex"
            iconSpan.style.alignItems = "center"
            iconSpan.style.justifyContent = "center"
            button.appendChild(iconSpan)

            accordionHeader.appendChild(button)

            const collapseDiv = doc.createElement("div")
            collapseDiv.id = collapseId
            collapseDiv.className = "accordion-collapse collapse"
            collapseDiv.setAttribute("aria-labelledby", headerId)
            collapseDiv.setAttribute("data-bs-parent", `#homeAccordion${containerIndex}`)

            const accordionBody = doc.createElement("div")
            accordionBody.className = "accordion-body"
            accordionBody.innerHTML = answerContainer.innerHTML

            collapseDiv.appendChild(accordionBody)

            item.innerHTML = ""
            item.appendChild(accordionHeader)
            item.appendChild(collapseDiv)

            itemIndex++
          }
        })
      })
    }

    // Fix relative image src paths, same as Menu Pages.
    const images = doc.querySelectorAll("img")
    images.forEach((img) => {
      const src = img.getAttribute("src")
      if (src && src.startsWith("..")) {
        const cleanedSrc = src.replace(/^(\.\.\/)+/, "")
        img.setAttribute("src", `${IMAGE_FETCH_BACKEND_URL}/${cleanedSrc}`)
      }
    })

    let finalHtml = doc.body.innerHTML
    finalHtml = finalHtml.replace(/(<p>\s*);/g, "$1")

    return { html: finalHtml, hasFaq }
  }

  useEffect(() => {
    if (homeContent?.long_description) {
      const { html, hasFaq } = processHtmlForAccordion(homeContent.long_description)
      setProcessedHtml(html || "")
      setHasFaqAccordion(hasFaq)
    } else {
      setProcessedHtml("")
      setHasFaqAccordion(false)
    }
  }, [homeContent])

  // Inject the scoped custom CSS into the page <head> whenever it changes.
  useEffect(() => {
    const styleTagId = "home-page-custom-css"
    let styleTag = document.getElementById(styleTagId) as HTMLStyleElement | null

    if (!homeContent?.custom_css) {
      if (styleTag) styleTag.remove()
      return
    }

    if (!styleTag) {
      styleTag = document.createElement("style")
      styleTag.id = styleTagId
      document.head.appendChild(styleTag)
    }
    styleTag.textContent = scopeCustomCss(homeContent.custom_css, `#${HOME_WRAPPER_ID}`)

    return () => {
      styleTag?.remove()
    }
  }, [homeContent?.custom_css])

  useEffect(() => {
    if (contentRef.current) {
      applyButtonGroupStyles(contentRef.current)
    }
  }, [processedHtml])

  return (
    <div className="bg-white">
      <MetaSection
        title={homeContent?.meta_title || "Plancrafter"}
        desc={homeContent?.meta_description || ""}
        keywords={homeContent?.meta_keyword || ""}
      />
      <Nav />
      <div className="subscription">
        <div className="container">
          <div id={HOME_WRAPPER_ID} ref={contentRef}>
            {homeContent === null && <div className="text-center my-5">Loading...</div>}
            <div dangerouslySetInnerHTML={{ __html: processedHtml || "" }} />
          </div>
        </div>
      </div>
      <Footer />
    </div>
  )

  
}

export default Home
